// client/src/components/AdminFeatureFlags.js

import React, { useState, useEffect } from 'react';
import { jwtDecode } from 'jwt-decode';
import {
  Typography,
  Box,
  Card,
  CardContent,
  Switch,
  FormControlLabel,
  Divider,
  Alert,
  Button,
  CircularProgress,
  Chip,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { useFeatureFlags } from '../contexts/FeatureFlagsContext';

/**
 * Component for managing feature flags in the admin dashboard
 */
const AdminFeatureFlags = () => {
  const { featureFlags, loading, error: contextError, updateFeatureFlag } = useFeatureFlags();
  const [localFlags, setLocalFlags] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  
  // Check if the user is an admin
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const decoded = jwtDecode(token);
        setIsAdmin(decoded.isAdmin === true);
      } catch (err) {
        console.error('Error decoding token:', err);
        setIsAdmin(false);
      }
    }
  }, []);
  
  // Initialize local flags from context when available
  if (featureFlags && !localFlags) {
    setLocalFlags({
      enableGamesFeature: featureFlags.enableGamesFeature !== false,
      enableConsolesFeature: featureFlags.enableConsolesFeature !== false,
      enableLegoFeature: featureFlags.enableLegoFeature !== false,
      enableMoviesFeature: featureFlags.enableMoviesFeature !== false,
      enableMusicFeature: featureFlags.enableMusicFeature !== false,
      enableBooksFeature: featureFlags.enableBooksFeature !== false,
      enableBoardGamesFeature: featureFlags.enableBoardGamesFeature || false,
      enableTradingCardsFeature: featureFlags.enableTradingCardsFeature || false,
      enableMangaFeature: featureFlags.enableMangaFeature || false,
      enableComicsFeature: featureFlags.enableComicsFeature || false,
      enableCustomCollections: featureFlags.enableCustomCollections || false,
      enableBacklog: featureFlags.enableBacklog !== false,
      enableDNF: featureFlags.enableDNF || false,
      enableStreaks: featureFlags.enableStreaks || false,
      enableFederation: featureFlags.enableFederation || false,
      // [seo-strip-start]
      enableDiscovery: featureFlags.enableDiscovery || false,
      // [seo-strip-end]
      enableDistribution: featureFlags.enableDistribution || false,
      enableQuotaHarvesting: featureFlags.enableQuotaHarvesting !== false,
      enableAiChatFeature: featureFlags.enableAiChatFeature || false,
      enableMessageBoardFeature: featureFlags.enableMessageBoardFeature || false,
      enableFollowSystem: featureFlags.enableFollowSystem || false,
      enableActivityFeed: featureFlags.enableActivityFeed || false,
      enableReputationSystem: featureFlags.enableReputationSystem || false,
      enableTrustModeration: featureFlags.enableTrustModeration || false,
      enableSuggestions: featureFlags.enableSuggestions || false,
      enableReviews: featureFlags.enableReviews || false,
      enableItemGallery: featureFlags.enableItemGallery || false,
      enableComments: featureFlags.enableComments || false,
      enableChatRooms: featureFlags.enableChatRooms || false,
      enableNotifications: featureFlags.enableNotifications || false,
      requireEmailVerification: featureFlags.requireEmailVerification || false,
      enableRicherReactions: featureFlags.enableRicherReactions || false,
    });
  }

  // Handle feature flag toggle
  const handleFeatureFlagChange = (flagName) => (event) => {
    if (!localFlags) return;
    
    setLocalFlags({
      ...localFlags,
      [flagName]: event.target.checked
    });
  };

  // Save feature flags
  const saveFeatureFlags = async () => {
    if (!localFlags) return;
    
    try {
      setSaving(true);
      setError(null);
      
      // Save each feature flag using the context function
      const results = await Promise.all(
        Object.entries(localFlags).map(async ([key, value]) => {
          const success = await updateFeatureFlag(key, value);
          return { key, success };
        })
      );
      
      // Check if all updates were successful
      const failures = results.filter(r => !r.success).map(r => r.key);
      
      if (failures.length === 0) {
        enqueueSnackbar('Feature flags saved successfully', { variant: 'success' });
      } else {
        const errorMessage = `Failed to update: ${failures.join(', ')}`;
        setError(errorMessage);
        enqueueSnackbar(errorMessage, { variant: 'error' });
      }
    } catch (err) {
      console.error('Error saving feature flags:', err);
      const errorMessage = 'Failed to save feature flags';
      setError(errorMessage);
      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  if (loading || !localFlags) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Feature Flags
        </Typography>
        
        <Typography variant="body2" color="text.secondary" paragraph>
          Enable or disable features across the application.
        </Typography>
        
        {(error || contextError) && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error || contextError}
          </Alert>
        )}
        
        <Box sx={{ mt: 2 }}>
          <FormControlLabel
            control={
              <Switch
                checked={localFlags.enableGamesFeature}
                onChange={handleFeatureFlagChange('enableGamesFeature')}
                color="primary"
                disabled={!isAdmin}
              />
            }
            label="Enable Games Collections"
          />
          <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 2 }}>
            When enabled, users can manage their video game collections and wishlists.
            {!isAdmin && <span style={{ color: 'red', fontWeight: 'bold' }}> (Admin privileges required to change this setting)</span>}
          </Typography>
        </Box>

        <Box sx={{ mt: 2 }}>
          <FormControlLabel
            control={
              <Switch
                checked={localFlags.enableConsolesFeature}
                onChange={handleFeatureFlagChange('enableConsolesFeature')}
                color="primary"
                disabled={!isAdmin}
              />
            }
            label="Enable Consoles Collections"
          />
          <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 2 }}>
            When enabled, users can manage their gaming console collections and wishlists.
            {!isAdmin && <span style={{ color: 'red', fontWeight: 'bold' }}> (Admin privileges required to change this setting)</span>}
          </Typography>
        </Box>

        <Box sx={{ mt: 2 }}>
          <FormControlLabel
            control={
              <Switch
                checked={localFlags.enableLegoFeature}
                onChange={handleFeatureFlagChange('enableLegoFeature')}
                color="primary"
                disabled={!isAdmin}
              />
            }
            label="Enable LEGO Collections"
          />
          <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 2 }}>
            When enabled, users can manage their LEGO set collections and wishlists.
            {!isAdmin && <span style={{ color: 'red', fontWeight: 'bold' }}> (Admin privileges required to change this setting)</span>}
          </Typography>
        </Box>

        <Box sx={{ mt: 2 }}>
          <FormControlLabel
            control={
              <Switch
                checked={localFlags.enableMoviesFeature}
                onChange={handleFeatureFlagChange('enableMoviesFeature')}
                color="primary"
                disabled={!isAdmin}
              />
            }
            label="Enable Movie Collections"
          />
          <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 2 }}>
            When enabled, users can manage collections of movies (DVDs, Blu-rays, 4K UHD, etc.).
            {!isAdmin && <span style={{ color: 'red', fontWeight: 'bold' }}> (Admin privileges required to change this setting)</span>}
          </Typography>
        </Box>
        
        <Box sx={{ mt: 2 }}>
          <FormControlLabel
            control={
              <Switch
                checked={localFlags.enableMusicFeature}
                onChange={handleFeatureFlagChange('enableMusicFeature')}
                color="primary"
                disabled={!isAdmin}
              />
            }
            label="Enable Music Collections"
          />
          <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 2 }}>
            When enabled, users can manage collections of music (CDs, vinyl records, cassettes, etc.).
            {!isAdmin && <span style={{ color: 'red', fontWeight: 'bold' }}> (Admin privileges required to change this setting)</span>}
          </Typography>
        </Box>

        <Box sx={{ mt: 2 }}>
          <FormControlLabel
            control={
              <Switch
                checked={localFlags.enableBooksFeature}
                onChange={handleFeatureFlagChange('enableBooksFeature')}
                color="primary"
                disabled={!isAdmin}
              />
            }
            label="Enable Book Collections"
          />
          <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 2 }}>
            When enabled, users can manage collections of books and track their reading list.
            {!isAdmin && <span style={{ color: 'red', fontWeight: 'bold' }}> (Admin privileges required to change this setting)</span>}
          </Typography>
        </Box>

        <Box sx={{ mt: 2 }}>
          <FormControlLabel
            control={
              <Switch
                checked={localFlags.enableBoardGamesFeature}
                onChange={handleFeatureFlagChange('enableBoardGamesFeature')}
                color="primary"
                disabled={!isAdmin}
              />
            }
            label="Enable Board Game Collections"
          />
          <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 2 }}>
            When enabled, users can manage their board game collections and wishlists.
            {!isAdmin && <span style={{ color: 'red', fontWeight: 'bold' }}> (Admin privileges required to change this setting)</span>}
          </Typography>
        </Box>

        <Box sx={{ mt: 2 }}>
          <FormControlLabel
            control={
              <Switch
                checked={localFlags.enableTradingCardsFeature}
                onChange={handleFeatureFlagChange('enableTradingCardsFeature')}
                color="primary"
                disabled={!isAdmin}
              />
            }
            label="Enable Trading Cards Collections"
          />
          <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 2 }}>
            When enabled, users can manage their trading card collections (Pokémon, MTG, Yu-Gi-Oh! and more).
            {!isAdmin && <span style={{ color: 'red', fontWeight: 'bold' }}> (Admin privileges required to change this setting)</span>}
          </Typography>
        </Box>

        <Box sx={{ mt: 2 }}>
          <FormControlLabel
            control={
              <Switch
                checked={localFlags.enableMangaFeature}
                onChange={handleFeatureFlagChange('enableMangaFeature')}
                color="primary"
                disabled={!isAdmin}
              />
            }
            label="Enable Manga Collections"
          />
          <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 2 }}>
            When enabled, users can manage their manga volume collections and wishlists.
            {!isAdmin && <span style={{ color: 'red', fontWeight: 'bold' }}> (Admin privileges required to change this setting)</span>}
          </Typography>
        </Box>

        <Box sx={{ mt: 2 }}>
          <FormControlLabel
            control={
              <Switch
                checked={localFlags.enableComicsFeature}
                onChange={handleFeatureFlagChange('enableComicsFeature')}
                color="primary"
                disabled={!isAdmin}
              />
            }
            label="Enable Comics Collections"
          />
          <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 2 }}>
            When enabled, users can manage their comic book issue collections and wishlists. Requires a ComicVine API key.
            {!isAdmin && <span style={{ color: 'red', fontWeight: 'bold' }}> (Admin privileges required to change this setting)</span>}
          </Typography>
        </Box>

        <Box sx={{ mt: 2 }}>
          <FormControlLabel
            control={
              <Switch
                checked={localFlags.enableBacklog}
                onChange={handleFeatureFlagChange('enableBacklog')}
                color="primary"
                disabled={!isAdmin}
              />
            }
            label="Enable Backlog"
          />
          <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 2 }}>
            When enabled, users can mark owned items as "in backlog" to track what they want to play, read, or listen to next.
            {!isAdmin && <span style={{ color: 'red', fontWeight: 'bold' }}> (Admin privileges required to change this setting)</span>}
          </Typography>
        </Box>

        <Box sx={{ mt: 2 }}>
          <FormControlLabel
            control={
              <Switch
                checked={localFlags.enableDNF}
                onChange={handleFeatureFlagChange('enableDNF')}
                color="primary"
                disabled={!isAdmin}
              />
            }
            label="Enable DNF &amp; Progress Tracking"
          />
          <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 2 }}>
            When enabled, users can mark items as Did Not Finish (DNF) and record how far they got (%, page, chapter, etc.).
            {!isAdmin && <span style={{ color: 'red', fontWeight: 'bold' }}> (Admin privileges required to change this setting)</span>}
          </Typography>
        </Box>

        <Box sx={{ mt: 2 }}>
          <FormControlLabel
            control={
              <Switch
                checked={localFlags.enableStreaks}
                onChange={handleFeatureFlagChange('enableStreaks')}
                color="primary"
                disabled={!isAdmin}
              />
            }
            label="Enable Streaks &amp; Goals"
          />
          <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 2 }}>
            When enabled, users build a daily collecting streak, set annual goals per collection type, view a full-year activity heat-map, and generate a year-in-review recap card.
            {!isAdmin && <span style={{ color: 'red', fontWeight: 'bold' }}> (Admin privileges required to change this setting)</span>}
          </Typography>
        </Box>

        <Box sx={{ mt: 2 }}>
          <FormControlLabel
            control={
              <Switch
                checked={localFlags.enableRicherReactions}
                onChange={handleFeatureFlagChange('enableRicherReactions')}
                color="primary"
                disabled={!isAdmin}
              />
            }
            label="Enable Richer Reactions"
          />
          <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 2 }}>
            Enables mood/vibe tags on reviews (pick up to 3 per review) and emoji reactions on the activity feed. Half-star ratings (0.5 increments) are always on regardless of this flag.
            {!isAdmin && <span style={{ color: 'red', fontWeight: 'bold' }}> (Admin privileges required to change this setting)</span>}
          </Typography>
        </Box>

        <Box sx={{ mt: 2 }}>
          <FormControlLabel
            control={
              <Switch
                checked={localFlags.enableCustomCollections}
                onChange={handleFeatureFlagChange('enableCustomCollections')}
                color="primary"
                disabled={!isAdmin}
              />
            }
            label="Enable Custom Collections"
          />
          <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 2 }}>
            When enabled, users can create their own collection types with custom fields and share them in a community library.
            {!isAdmin && <span style={{ color: 'red', fontWeight: 'bold' }}> (Admin privileges required to change this setting)</span>}
          </Typography>
        </Box>

        <Box sx={{ mt: 2 }}>
          <FormControlLabel
            control={
              <Switch
                checked={localFlags.enableFederation}
                onChange={handleFeatureFlagChange('enableFederation')}
                color="primary"
                disabled={!isAdmin}
              />
            }
            label="Federation (ActivityPub)"
          />
          <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 2 }}>
            When enabled, this instance can federate with other InterCollect instances via ActivityPub. Configure domain and instance details in Federation Settings.
            {!isAdmin && <span style={{ color: 'red', fontWeight: 'bold' }}> (Admin privileges required to change this setting)</span>}
          </Typography>
        </Box>

        {/* [seo-strip-start] */}
        <Box sx={{ mt: 2 }}>
          <FormControlLabel
            control={
              <Switch
                checked={localFlags.enableDiscovery}
                onChange={handleFeatureFlagChange('enableDiscovery')}
                color="primary"
                disabled={!isAdmin}
              />
            }
            label="Discovery (SEO & About Page)"
          />
          <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 2 }}>
            When enabled, shows the public /about page, "What is InterCollect?" link on login, and activates the sitemap. Configure content in Admin → SEO & Discovery.
            {!isAdmin && <span style={{ color: 'red', fontWeight: 'bold' }}> (Admin privileges required to change this setting)</span>}
          </Typography>
        </Box>
        {/* [seo-strip-end] */}

        <Box sx={{ mt: 2 }}>
          <FormControlLabel
            control={
              <Switch
                checked={localFlags.enableDistribution}
                onChange={handleFeatureFlagChange('enableDistribution')}
                color="primary"
                disabled={!isAdmin}
              />
            }
            label="Self-Distribution (Download Page & Registry)"
          />
          <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 2 }}>
            When enabled, shows the public /download page with docker-compose.yml and source archive. Requires running the registry service and generating a distribution with scripts/generateDistribution.js.
            {!isAdmin && <span style={{ color: 'red', fontWeight: 'bold' }}> (Admin privileges required to change this setting)</span>}
          </Typography>
        </Box>

        <Box sx={{ mt: 2 }}>
          <FormControlLabel
            control={
              <Switch
                checked={localFlags.enableQuotaHarvesting}
                onChange={handleFeatureFlagChange('enableQuotaHarvesting')}
                color="primary"
                disabled={!isAdmin}
              />
            }
            label="Enable API Quota Harvesting"
          />
          <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 2 }}>
            When enabled, background jobs proactively build the local catalogue by harvesting external APIs during spare quota windows. Disable to stop all harvest background jobs.
            {!isAdmin && <span style={{ color: 'red', fontWeight: 'bold' }}> (Admin privileges required to change this setting)</span>}
          </Typography>
        </Box>

        <Box sx={{ mt: 2 }}>
          <FormControlLabel
            control={
              <Switch
                checked={localFlags.enableAiChatFeature}
                onChange={handleFeatureFlagChange('enableAiChatFeature')}
                color="primary"
                disabled={!isAdmin}
              />
            }
            label={<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>Enable AI Chat Assistant<Chip label="Work In Progress" size="small" color="warning" /></Box>}
          />
          <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 2 }}>
            AI Chat Assistant is currently not functional. When enabled, users can access an AI chat assistant from any page in the application.
            {!isAdmin && <span style={{ color: 'red', fontWeight: 'bold' }}> (Admin privileges required to change this setting)</span>}
          </Typography>
        </Box>

        <Box sx={{ mt: 2 }}>
          <FormControlLabel
            control={
              <Switch
                checked={localFlags.enableMessageBoardFeature}
                onChange={handleFeatureFlagChange('enableMessageBoardFeature')}
                color="primary"
                disabled={!isAdmin}
              />
            }
            label="Enable Message Board"
          />
          <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 2 }}>
            When enabled, users can message collection owners and discuss items.
            {!isAdmin && <span style={{ color: 'red', fontWeight: 'bold' }}> (Admin privileges required to change this setting)</span>}
          </Typography>
        </Box>
        
        <Divider sx={{ my: 2 }} />

        <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1, mb: 1 }}>
          Community Features
        </Typography>

        <Box sx={{ mt: 2 }}>
          <FormControlLabel
            control={
              <Switch
                checked={localFlags.enableFollowSystem}
                onChange={handleFeatureFlagChange('enableFollowSystem')}
                color="primary"
                disabled={!isAdmin}
              />
            }
            label="Enable Follow System"
          />
          <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 2 }}>
            When enabled, users can follow each other and see follower/following counts.
          </Typography>
        </Box>

        <Box sx={{ mt: 2 }}>
          <FormControlLabel
            control={
              <Switch
                checked={localFlags.enableActivityFeed}
                onChange={handleFeatureFlagChange('enableActivityFeed')}
                color="primary"
                disabled={!isAdmin}
              />
            }
            label="Enable Activity Feed"
          />
          <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 2 }}>
            When enabled, users see a feed of activity from people they follow.
          </Typography>
        </Box>

        <Box sx={{ mt: 2 }}>
          <FormControlLabel
            control={
              <Switch
                checked={localFlags.enableReputationSystem}
                onChange={handleFeatureFlagChange('enableReputationSystem')}
                color="primary"
                disabled={!isAdmin}
              />
            }
            label="Enable Reputation System"
          />
          <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 2 }}>
            When enabled, users earn reputation points and trust levels for community contributions.
          </Typography>
        </Box>

        <Box sx={{ mt: 2 }}>
          <FormControlLabel
            control={
              <Switch
                checked={localFlags.enableTrustModeration}
                onChange={handleFeatureFlagChange('enableTrustModeration')}
                color="primary"
                disabled={!isAdmin}
              />
            }
            label="Enable Trust-Based Moderation"
          />
          <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 2 }}>
            When enabled, trusted users (Level 4+) can review reported content.
          </Typography>
        </Box>

        <Box sx={{ mt: 2 }}>
          <FormControlLabel
            control={
              <Switch
                checked={localFlags.enableReviews}
                onChange={handleFeatureFlagChange('enableReviews')}
                color="primary"
                disabled={!isAdmin}
              />
            }
            label="Enable Community Reviews"
          />
          <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 2 }}>
            When enabled, users can write and read reviews for items in their collections.
          </Typography>
        </Box>

        <Box sx={{ mt: 2 }}>
          <FormControlLabel
            control={
              <Switch
                checked={localFlags.enableItemGallery}
                onChange={handleFeatureFlagChange('enableItemGallery')}
                color="primary"
                disabled={!isAdmin}
              />
            }
            label="Enable Item Gallery (Photo Uploads)"
          />
          <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 2 }}>
            When enabled, users can upload photos of their actual items and share them.
          </Typography>
        </Box>

        <Box sx={{ mt: 2 }}>
          <FormControlLabel
            control={
              <Switch
                checked={localFlags.enableComments}
                onChange={handleFeatureFlagChange('enableComments')}
                color="primary"
                disabled={!isAdmin}
              />
            }
            label="Enable Comments"
          />
          <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 2 }}>
            When enabled, users can comment on reviews and photos.
          </Typography>
        </Box>

        <Box sx={{ mt: 2 }}>
          <FormControlLabel
            control={
              <Switch
                checked={localFlags.enableSuggestions}
                onChange={handleFeatureFlagChange('enableSuggestions')}
                color="primary"
                disabled={!isAdmin}
              />
            }
            label="Enable Item Suggestions"
          />
          <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 2 }}>
            When enabled, users see personalised item suggestions based on their collection and ratings.
          </Typography>
        </Box>

        <Box sx={{ mt: 2 }}>
          <FormControlLabel
            control={
              <Switch
                checked={localFlags.enableChatRooms}
                onChange={handleFeatureFlagChange('enableChatRooms')}
                color="primary"
                disabled={!isAdmin}
              />
            }
            label="Enable Chat Rooms"
          />
          <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 2 }}>
            When enabled, users can join public chat rooms to discuss collections.
          </Typography>
        </Box>

        <Box sx={{ mt: 2 }}>
          <FormControlLabel
            control={
              <Switch
                checked={localFlags.enableNotifications}
                onChange={handleFeatureFlagChange('enableNotifications')}
                color="primary"
                disabled={!isAdmin}
              />
            }
            label="Enable Notifications"
          />
          <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 2 }}>
            When enabled, users see a notification bell in the AppBar and a notifications page showing activity and personal alerts.
          </Typography>
        </Box>

        <Divider sx={{ my: 2 }} />

        <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1, mb: 1 }}>
          Security
        </Typography>

        <Box sx={{ mt: 2 }}>
          <FormControlLabel
            control={
              <Switch
                checked={localFlags.requireEmailVerification}
                onChange={handleFeatureFlagChange('requireEmailVerification')}
                color="primary"
                disabled={!isAdmin}
              />
            }
            label="Require Email Verification"
          />
          <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 2 }}>
            When enabled, users must verify their email address before accessing protected features.
          </Typography>
        </Box>

        <Divider sx={{ my: 2 }} />
        
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', flexDirection: 'column', alignItems: 'flex-end' }}>
          {!isAdmin && (
            <Typography variant="body2" color="error" sx={{ mb: 1 }}>
              Note: You need admin privileges to save these settings permanently.
              Changes will only apply to your current session.
            </Typography>
          )}
          <Button
            variant="contained"
            color="primary"
            onClick={saveFeatureFlags}
            disabled={saving}
            startIcon={saving ? <CircularProgress size={20} /> : null}
          >
            {saving ? 'Saving...' : isAdmin ? 'Save Changes' : 'Apply Changes for Current Session'}
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
};

export default AdminFeatureFlags;
